/**
 * Program Skills & Abilities — shared client model + pure helpers.
 *
 * A program may define its OWN predefined list of skills (admin-managed,
 * per program — a skill configured on one program never appears on
 * another unless separately configured there). Applicants pick from the
 * active list of THEIR program under the program's selection rule:
 *
 *   - selectionMode 'single'   → exactly one skill
 *   - selectionMode 'multiple' → any number up to `maxSelections`;
 *                                `maxSelections: null` (blank in the
 *                                editor) means UNLIMITED.
 *
 * Selections are stored on the application record as
 * `selectedSkills: [{ skillId, label }]` (label = snapshot taken at
 * selection time, refreshed to the current config label while the skill
 * stays active) plus `skillIds: string[]` for querying. Removing a skill
 * in the admin editor is a DEACTIVATION (`active: false`), never a hard
 * delete, so stored selections and historical records are never silently
 * destroyed; display resolution falls back to the snapshot label for a
 * deactivated/removed skill.
 *
 * The server enforces the same rules via server/programSkillsCore.ts —
 * this module is the client mirror used for rendering + pre-validation.
 */

export interface ProgramSkillOption {
  id: string;
  label: string;
  active: boolean;
}

export interface ProgramSkillsConfig {
  selectionMode: 'single' | 'multiple';
  /** Only meaningful in 'multiple' mode; null = unlimited. */
  maxSelections: number | null;
  skills: ProgramSkillOption[];
}

export interface SelectedSkill {
  skillId: string;
  label: string;
}

export const EMPTY_SKILLS_CONFIG: ProgramSkillsConfig = {
  selectionMode: 'multiple',
  maxSelections: null,
  skills: [],
};

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** Generate an id for a newly added skill option (unique within the program). */
export function newSkillId(): string {
  return `sk_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Normalise ANY historical/loose shape into a valid ProgramSkillsConfig.
 * - skill rows without a usable id or label are dropped;
 * - duplicate ids keep the first row;
 * - `active` defaults to true (only an explicit false deactivates);
 * - maxSelections: a positive integer is kept; blank/invalid → null
 *   (unlimited) in multiple mode, and forced to 1 in single mode.
 */
export function sanitizeSkillsConfig(raw: unknown): ProgramSkillsConfig {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const selectionMode = data.selectionMode === 'single' ? 'single' : 'multiple';
  let maxSelections: number | null = null;
  if (selectionMode === 'single') {
    maxSelections = 1;
  } else if (typeof data.maxSelections === 'number' && Number.isFinite(data.maxSelections) && data.maxSelections > 0) {
    maxSelections = Math.floor(data.maxSelections);
  }
  const seen = new Set<string>();
  const skills: ProgramSkillOption[] = [];
  if (Array.isArray(data.skills)) {
    for (const row of data.skills as Array<Record<string, unknown>>) {
      if (!row || typeof row !== 'object') continue;
      const id = asString(row.id).trim();
      const label = asString(row.label).trim();
      if (!id || !label || seen.has(id)) continue;
      seen.add(id);
      skills.push({ id, label, active: row.active !== false });
    }
  }
  return { selectionMode, maxSelections, skills };
}

/** The skills an applicant/employer may pick right now. */
export function activeSkills(config: ProgramSkillsConfig | undefined | null): ProgramSkillOption[] {
  if (!config) return [];
  return config.skills.filter((s) => s.active);
}

/** True when the program has a configured (non-empty, active) skill list. */
export function programHasActiveSkills(config: ProgramSkillsConfig | undefined | null): boolean {
  return activeSkills(config).length > 0;
}

/** The most skills an applicant may hold under this config (Infinity = unlimited). */
export function selectionLimit(config: ProgramSkillsConfig): number {
  if (config.selectionMode === 'single') return 1;
  return config.maxSelections == null ? Number.POSITIVE_INFINITY : config.maxSelections;
}

/**
 * Validate a candidate selection against a program config.
 * Returns the canonical selection (deduped, labels refreshed from the
 * config for skills that are still active) or an error message.
 * A selection naming a DEACTIVATED skill id is rejected for NEW picks —
 * stored historical selections are never passed through here for edits
 * that keep them; display resolution handles those (resolveSkillLabel).
 */
export function validateSkillSelection(
  config: ProgramSkillsConfig | undefined | null,
  selection: unknown,
): { ok: true; selection: SelectedSkill[] } | { ok: false; error: string } {
  const cfg = sanitizeSkillsConfig(config ?? undefined);
  const active = new Map(activeSkills(cfg).map((s) => [s.id, s]));
  if (!Array.isArray(selection)) {
    return { ok: false, error: 'Skills selection must be a list.' };
  }
  const out: SelectedSkill[] = [];
  const seen = new Set<string>();
  for (const row of selection as Array<Record<string, unknown>>) {
    if (!row || typeof row !== 'object') return { ok: false, error: 'Skills selection contains an invalid entry.' };
    const skillId = asString((row as any).skillId ?? (row as any).id).trim();
    if (!skillId) return { ok: false, error: 'Skills selection contains an entry without a skill id.' };
    if (seen.has(skillId)) return { ok: false, error: 'Skills selection contains a duplicate skill.' };
    seen.add(skillId);
    const option = active.get(skillId);
    if (!option) {
      return { ok: false, error: 'Skills selection contains a skill that is not available for this program.' };
    }
    out.push({ skillId, label: option.label });
  }
  const limit = selectionLimit(cfg);
  if (out.length > limit) {
    return {
      ok: false,
      error: cfg.selectionMode === 'single'
        ? 'Only one skill may be selected for this program.'
        : `You can select up to ${limit} skills for this program.`,
    };
  }
  return { ok: true, selection: out };
}

/**
 * Normalise a STORED selection for display/persistence round-trips:
 * keeps every stored entry (including ones whose skill was later
 * deactivated or removed from the config), dedupes by id, and resolves
 * each label to the CURRENT config label when the skill still exists in
 * the config (active or not), else the stored snapshot label.
 */
export function normaliseStoredSelection(
  config: ProgramSkillsConfig | undefined | null,
  stored: unknown,
): SelectedSkill[] {
  if (!Array.isArray(stored)) return [];
  const byId = new Map<string, ProgramSkillOption>();
  if (config) for (const s of config.skills) byId.set(s.id, s);
  const out: SelectedSkill[] = [];
  const seen = new Set<string>();
  for (const row of stored as Array<Record<string, unknown>>) {
    if (!row || typeof row !== 'object') continue;
    const skillId = asString((row as any).skillId ?? (row as any).id).trim();
    if (!skillId || seen.has(skillId)) continue;
    seen.add(skillId);
    const snapshot = asString((row as any).label).trim();
    const current = byId.get(skillId);
    out.push({ skillId, label: current?.label || snapshot || skillId });
  }
  return out;
}

export function skillIdsOf(selection: SelectedSkill[]): string[] {
  return selection.map((s) => s.skillId);
}
